/* eslint-disable */
/**
 * ═════════════════════════════════════════════════════════════════════════
 *   Cocoon Nest — CheckInEngine (IMMUTABLE)
 *   Iter-300 · Feb 2026 · "Break-the-Glass" Part 2 — isolation of the
 *   Guest Check-In wizard's step validation, profile / medical / massage
 *   data-sync, phone (E.164) enforcement, and QR-token minting logic.
 *
 *   Iter-303 · Feb 2026 · Standardized Nationality + Country Search.
 *   The old cmdk Popover → native `<datalist>` chain (Iter-298) had two
 *   real-world failures on iPad Safari: (a) some iPads never popped the
 *   dropdown until the guest typed a full letter, and (b) the option list
 *   had no visible dropdown arrow so guests didn't know it was searchable
 *   at all. React-Select ships a proper controlled combobox with a
 *   visible dropdown indicator, instant filter-as-you-type, and a
 *   48-px touch target on every option — the definitive fix.
 * ═════════════════════════════════════════════════════════════════════════
 *
 *   THIS FILE IS IMMUTABLE.
 *
 *   Rules for future agents / humans (also recorded in SYSTEM_RULES.md):
 *     1. Every rule that gates the Guest Check-In wizard — step
 *        validation, canonical Nationality / Country lookup, E.164
 *        WhatsApp enforcement, medical / massage preview→state
 *        mapping, QR token mint + poll — lives here.
 *     2. This file MUST NOT be edited or refactored as part of a UI
 *        task, a design pass, an ESLint autofix, a Prettier reformat,
 *        or any "cleanup" pass. If a bug is genuinely inside the
 *        engine, open a dedicated task for it and update SYSTEM_RULES.
 *     3. Consumers (`CustomerCheckIn.jsx`, `CheckInDialog.jsx`, any
 *        future kiosk / tablet / self-service surface) MUST call the
 *        exported helpers rather than re-implementing the checks
 *        inline. Inline check-in validation anywhere else is a P0.
 *     4. NO AUTOMATED TEXT INJECTION during medical reviews. The
 *        `extractMedicalFromPreview` helper deep-clones the guest's
 *        last-visit answers verbatim; no sentinels, no auto-appended
 *        "confirmed" strings, no default answers. Only a human edit
 *        may mutate a medical field.
 *
 *   Nationality + Country combobox contract (Iter-303):
 *     Both pickers are backed by `react-select` v5. Reverting to cmdk,
 *     Radix Popover, or the previous native `<datalist>` is a P0
 *     regression — the datalist variant crashed on some iPad Safari
 *     builds and did not render a visible dropdown arrow. React-Select
 *     is the ONLY approved toolkit for these two fields; do not swap
 *     it for `@radix-ui/react-select`, `downshift`, `cmdk`, or a fresh
 *     native `<select>`.
 *
 *   Phone contract:
 *     WhatsApp intake uses the shared `PhoneInput` (re-exported as
 *     `CheckinPhoneInput`) — country flag stays global (Bahrain +973,
 *     Italy +39, UK +44, etc.) and the field always emits E.164
 *     `+<dial><digits>` via `composePhone`. Validation via
 *     `isValidWhatsappE164` guarantees no bare local number reaches
 *     `POST /auth/customer/checkin/complete`.
 * ─────────────────────────────────────────────────────────────────────
 */

import React, { useMemo } from "react";
import Select from "react-select";
import { staffApi } from "@/lib/api";
import { NATIONALITIES } from "@/lib/nationalities";
import { COUNTRIES } from "@/lib/countries";
import { composePhone } from "@/lib/countryCodes";

// ── Component re-exports (single source of truth) ──────────────────
// Every check-in surface imports these THROUGH the engine so a swap
// (e.g. new locale-aware picker) is a one-file change here.
export { default as CheckinPhoneInput } from "@/components/forms/PhoneInput";

// ── Full ISO 3166-1 country list (canonical short names) ───────────
// Iter-303: replaces the previous 90-entry `COUNTRIES` list on the
// Nationality + Country pickers. The old list was tuned for phone
// dial-codes; the check-in picker needs the FULL global set so a
// guest from Bhutan / Fiji / São Tomé can pick their real country.
// Alphabetical by canonical short name. This list is used ONLY by
// the CountryCombobox render — the phone-flag picker still uses the
// dial-code-aware `COUNTRIES` from `@/lib/countries`.
export const ISO_COUNTRIES = [
  "Afghanistan","Åland Islands","Albania","Algeria","American Samoa","Andorra","Angola","Anguilla","Antarctica","Antigua and Barbuda","Argentina","Armenia","Aruba","Australia","Austria","Azerbaijan",
  "Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bermuda","Bhutan","Bolivia","Bonaire, Sint Eustatius and Saba","Bosnia and Herzegovina","Botswana","Bouvet Island","Brazil","British Indian Ocean Territory","Brunei Darussalam","Bulgaria","Burkina Faso","Burundi",
  "Cabo Verde","Cambodia","Cameroon","Canada","Cayman Islands","Central African Republic","Chad","Chile","China","Christmas Island","Cocos (Keeling) Islands","Colombia","Comoros","Congo","Congo, Democratic Republic of the","Cook Islands","Costa Rica","Côte d'Ivoire","Croatia","Cuba","Curaçao","Cyprus","Czechia",
  "Denmark","Djibouti","Dominica","Dominican Republic",
  "Ecuador","Egypt","El Salvador","Equatorial Guinea","Eritrea","Estonia","Eswatini","Ethiopia",
  "Falkland Islands (Malvinas)","Faroe Islands","Fiji","Finland","France","French Guiana","French Polynesia","French Southern Territories",
  "Gabon","Gambia","Georgia","Germany","Ghana","Gibraltar","Greece","Greenland","Grenada","Guadeloupe","Guam","Guatemala","Guernsey","Guinea","Guinea-Bissau","Guyana",
  "Haiti","Heard Island and McDonald Islands","Holy See","Honduras","Hong Kong","Hungary",
  "Iceland","India","Indonesia","Iran","Iraq","Ireland","Isle of Man","Israel","Italy",
  "Jamaica","Japan","Jersey","Jordan",
  "Kazakhstan","Kenya","Kiribati","Korea, Democratic People's Republic of","Korea, Republic of","Kuwait","Kyrgyzstan",
  "Lao People's Democratic Republic","Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein","Lithuania","Luxembourg",
  "Macao","Madagascar","Malawi","Malaysia","Maldives","Mali","Malta","Marshall Islands","Martinique","Mauritania","Mauritius","Mayotte","Mexico","Micronesia, Federated States of","Moldova","Monaco","Mongolia","Montenegro","Montserrat","Morocco","Mozambique","Myanmar",
  "Namibia","Nauru","Nepal","Netherlands","New Caledonia","New Zealand","Nicaragua","Niger","Nigeria","Niue","Norfolk Island","North Macedonia","Northern Mariana Islands","Norway",
  "Oman",
  "Pakistan","Palau","Palestine, State of","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Pitcairn","Poland","Portugal","Puerto Rico",
  "Qatar",
  "Réunion","Romania","Russian Federation","Rwanda",
  "Saint Barthélemy","Saint Helena, Ascension and Tristan da Cunha","Saint Kitts and Nevis","Saint Lucia","Saint Martin (French part)","Saint Pierre and Miquelon","Saint Vincent and the Grenadines","Samoa","San Marino","Sao Tome and Principe","Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone","Singapore","Sint Maarten (Dutch part)","Slovakia","Slovenia","Solomon Islands","Somalia","South Africa","South Georgia and the South Sandwich Islands","South Sudan","Spain","Sri Lanka","Sudan","Suriname","Svalbard and Jan Mayen","Sweden","Switzerland","Syrian Arab Republic",
  "Taiwan","Tajikistan","Tanzania","Thailand","Timor-Leste","Togo","Tokelau","Tonga","Trinidad and Tobago","Tunisia","Türkiye","Turkmenistan","Turks and Caicos Islands","Tuvalu",
  "Uganda","Ukraine","United Arab Emirates","United Kingdom","United States","United States Minor Outlying Islands","Uruguay","Uzbekistan",
  "Vanuatu","Venezuela","Viet Nam","Virgin Islands (British)","Virgin Islands (U.S.)",
  "Wallis and Futuna","Western Sahara",
  "Yemen",
  "Zambia","Zimbabwe",
];

// Validation set — union of the ISO list and the phone-dial list so a
// legacy guest whose profile stored a phone-list-only variant (e.g.
// "United States" both appear) still validates. Legacy free-text
// values (typos) still fail, preserving the canonical-value contract.
const COUNTRY_NAMES = new Set([
  ...ISO_COUNTRIES,
  ...COUNTRIES.map((c) => c.name),
]);

// ── React-Select shared styling (iPad-tuned, Iter-303) ─────────────
// 56 px min-height, 16 px text (iOS never zooms), 48 px per option so
// every row is a comfortable touch target on an iPad in portrait.
// Menu is portalled to `document.body` so a modal / drawer parent
// cannot clip the dropdown. Dropdown arrow is intentionally left
// visible (react-select default) — the previous datalist build had
// no arrow and guests didn't realise the field was searchable.
const SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 56,
    borderRadius: 8,
    borderColor: state.isFocused ? "hsl(var(--ring))" : "hsl(var(--input))",
    boxShadow: state.isFocused ? "0 0 0 2px hsl(var(--ring))" : "none",
    backgroundColor: "hsl(var(--background))",
    cursor: "text",
    ":hover": { borderColor: state.isFocused ? "hsl(var(--ring))" : "hsl(var(--input))" },
  }),
  valueContainer: (base) => ({ ...base, padding: "4px 14px" }),
  input: (base) => ({ ...base, fontSize: 16, color: "hsl(var(--foreground))", margin: 0, padding: 0 }),
  singleValue: (base) => ({ ...base, fontSize: 16, color: "hsl(var(--foreground))" }),
  placeholder: (base) => ({ ...base, fontSize: 16, color: "hsl(var(--muted-foreground))" }),
  option: (base, state) => ({
    ...base,
    minHeight: 48,
    padding: "12px 16px",
    fontSize: 16,
    lineHeight: "24px",
    cursor: "pointer",
    color: "hsl(var(--foreground))",
    backgroundColor: state.isSelected
      ? "hsl(var(--accent))"
      : state.isFocused
        ? "hsl(var(--accent) / 0.5)"
        : "transparent",
    ":active": { backgroundColor: "hsl(var(--accent))" },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 8,
    overflow: "hidden",
    border: "1px solid hsl(var(--border))",
    backgroundColor: "hsl(var(--background))",
    boxShadow: "0 10px 30px hsl(0 0% 0% / 0.12)",
  }),
  menuList: (base) => ({ ...base, maxHeight: 320, paddingTop: 4, paddingBottom: 4 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: "hsl(var(--muted-foreground))",
    padding: 10,
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "none",
    transition: "transform 120ms ease",
  }),
  clearIndicator: (base) => ({ ...base, color: "hsl(var(--muted-foreground))", padding: 10 }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: "hsl(var(--border))" }),
  noOptionsMessage: (base) => ({ ...base, padding: "12px 16px", fontSize: 15, color: "hsl(var(--muted-foreground))" }),
};

// Small helper: convert a plain string (possibly a legacy free-text
// value not in the canonical list) into the shape react-select expects
// for a controlled value. Returning the same object shape for both
// "in-list" and "legacy" values means a stale profile still renders
// its stored text — the parent's Step-1 gate is what blocks a typo
// from being submitted.
const _valueToOption = (v) => (v ? { value: v, label: v } : null);

// ── NationalityCombobox (Iter-303) ────────────────────────────────
export function NationalityCombobox({
  value,
  onChange,
  placeholder = "Type to search nationality…",
  testId = "nationality-combobox",
}) {
  const options = useMemo(
    () => NATIONALITIES.map((n) => ({ value: n, label: n })),
    []
  );
  return (
    <div data-testid={testId}>
      <Select
        inputId={`${testId}-input`}
        classNamePrefix="rs-nationality"
        options={options}
        value={_valueToOption(value)}
        onChange={(opt) => onChange(opt ? opt.value : "")}
        placeholder={placeholder}
        isClearable
        isSearchable
        openMenuOnFocus
        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
        menuPosition="fixed"
        styles={SELECT_STYLES}
        aria-label="Nationality"
        noOptionsMessage={() => "No matches"}
      />
    </div>
  );
}

// ── CountryCombobox (Iter-303) ────────────────────────────────────
// Options come from the full ISO 3166-1 canonical list so every
// country of residence is reachable from the check-in wizard.
export function CountryCombobox({
  value,
  onChange,
  placeholder = "Type to search country…",
  testId = "country-combobox",
}) {
  const options = useMemo(
    () => ISO_COUNTRIES.map((n) => ({ value: n, label: n })),
    []
  );
  return (
    <div data-testid={testId}>
      <Select
        inputId={`${testId}-input`}
        classNamePrefix="rs-country"
        options={options}
        value={_valueToOption(value)}
        onChange={(opt) => onChange(opt ? opt.value : "")}
        placeholder={placeholder}
        isClearable
        isSearchable
        openMenuOnFocus
        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
        menuPosition="fixed"
        styles={SELECT_STYLES}
        aria-label="Country of residence"
        noOptionsMessage={() => "No matches"}
      />
    </div>
  );
}

// ── Intake constants ────────────────────────────────────────────────
// "How did you hear about us?" origin keys. Extracted from
// `CustomerCheckIn.jsx` unchanged so behaviour is byte-for-byte
// identical. The picker resolves to either `{ source }` or
// `{ source: "referral", referrerId }`.
export const HEAR_OPTIONS = [
  { key: "instagram", label: "Instagram" },
  { key: "facebook",  label: "Facebook" },
  { key: "google",    label: "Google search" },
  { key: "walk_in",   label: "Walked past the spa" },
  { key: "other",     label: "Other" },
];

// Gender is strictly Male / Female per the Feb-2026 medical intake
// policy. The old non-binary / prefer-not-say options were removed
// because clinicians need a definitive value on the record.
export const GENDER_OPTIONS = [
  { key: "female", label: "Female" },
  { key: "male",   label: "Male" },
];

// Massage Preference Card — every one of these 11 health toggles MUST
// carry a yes/no answer, and any "yes" MUST have an explanatory note.
const MASSAGE_HEALTH_REQUIRED = [
  "heart_conditions", "muscular_pain", "joint_problem", "blood_pressure",
  "pregnant", "asthma", "claustrophobia", "recent_injury", "allergies",
  "recent_surgery", "other",
];

// ── Canonical-value validators ──────────────────────────────────────
// Both dropdowns MUST hold a value from the canonical list — free-text
// typos can never satisfy the mandatory requirement. Guards against a
// client that pre-loaded a stale free-text value from a legacy profile.
export const isValidNationality = (v) => NATIONALITIES.includes((v || "").trim() ? v : "");
export const isValidCountry = (v) => COUNTRY_NAMES.has(v);
export const isValidEmail = (v) => /^\S+@\S+\.\S+$/.test((v || "").trim());

// ── Phone (E.164) enforcement ──────────────────────────────────────
// Applies globally to all 8 branches: the WhatsApp field emits
// `+<dial><digits>` via `PhoneInput`, and this validator ensures a
// bare local number (no leading `+`, no dial code) never satisfies
// the mandatory requirement — the awards / notifications pipeline
// relies on the E.164 form.
export const isValidWhatsappE164 = (v) => {
  const s = (v || "").trim();
  if (!s.startsWith("+")) return false;
  const digits = s.slice(1).replace(/\D/g, "");
  // Dial code (1-3 digits) + subscriber number (≥5 digits) ⇒ ≥6.
  return digits.length >= 6;
};

// Convenience re-export — a caller can compose a raw phone number
// directly through the engine's surface rather than reaching into
// `@/lib/countryCodes` from a check-in file.
export const toE164 = composePhone;

// ── Preview → local state mappers ───────────────────────────────────
// `POST /auth/customer/activate/preview` returns the server's view of
// the guest. These helpers turn that payload into the exact shapes the
// wizard renders. Pure — no React state, no I/O.

export const extractProfileFromPreview = (preview) => {
  const p = preview || {};
  let hearSource = "";
  let hearLabel = "";
  if (p.referral_source) {
    hearSource = p.referral_source;
    const src = HEAR_OPTIONS.find((h) => h.key === p.referral_source);
    hearLabel = src
      ? src.label
      : (p.referral_source === "referral" ? "Referred by a friend" : p.referral_source);
  }
  return {
    firstName: p.first_name || "",
    lastName: p.last_name || "",
    gender: p.gender || "",
    dob: p.dob || "",
    nationality: p.nationality || "",
    country: p.country || "",
    email: p.email || "",
    whatsapp: p.whatsapp || "",
    hearSource,
    hearLabel,
  };
};

// Deep-clone the guest's last-visit medical answers so state mutations
// never mutate the incoming preview payload. Only present for returning
// guests (`skip_reason == "returning_guest"`) — returns `null` for
// anyone else so the caller knows to leave its blank defaults alone.
export const extractMedicalFromPreview = (preview) => {
  const pm = preview?.previous_medical;
  if (!pm) return null;
  return {
    medication_allergies: pm.medication_allergies || "",
    cosmetic_allergies: Array.isArray(pm.cosmetic_allergies) ? [...pm.cosmetic_allergies] : [],
    current_medications: pm.current_medications || "",
    medical_questionnaire: pm.medical_questionnaire && typeof pm.medical_questionnaire === "object"
      ? JSON.parse(JSON.stringify(pm.medical_questionnaire))
      : {},
  };
};

// Prior-massage prefill — returning wellness guests keep their pressure
// / mood sliders / body-map picks from last time. The disclaimer must
// always be re-agreed on each check-in, so it's forced back to false.
export const extractMassageFromPreview = (preview) => {
  const pmp = preview?.previous_massage_preferences;
  if (!pmp) return null;
  return {
    pressure: pmp.pressure || "",
    mood_physically: typeof pmp.mood_physically === "number" ? pmp.mood_physically : 5,
    mood_emotionally: typeof pmp.mood_emotionally === "number" ? pmp.mood_emotionally : 5,
    avoid_areas: Array.isArray(pmp.avoid_areas) ? [...pmp.avoid_areas] : [],
    focus_areas: Array.isArray(pmp.focus_areas) ? [...pmp.focus_areas] : [],
    health_conditions: pmp.health_conditions && typeof pmp.health_conditions === "object"
      ? JSON.parse(JSON.stringify(pmp.health_conditions))
      : {},
    agreed_disclaimer: false,
  };
};

// ── Medical questionnaire mutators ──────────────────────────────────
// Immutable helpers used by the Yes/No radio + note textarea. A human
// on-screen edit is the ONLY path that mutates a medical field —
// these helpers preserve every other question's answer + note verbatim
// so no key is silently reset.

export const withMedicalAnswer = (medical, key, answer) => ({
  ...medical,
  medical_questionnaire: {
    ...(medical?.medical_questionnaire || {}),
    [key]: { ...(medical?.medical_questionnaire?.[key] || {}), answer },
  },
});

export const withMedicalNote = (medical, key, note) => ({
  ...medical,
  medical_questionnaire: {
    ...(medical?.medical_questionnaire || {}),
    [key]: { ...(medical?.medical_questionnaire?.[key] || {}), note },
  },
});

// ── Step validation ─────────────────────────────────────────────────
// Pure. Returns `true` iff every mandatory Step-1 field is satisfied.
// Password + Terms are gated only for brand-new guests — returning
// Active Members in Medical Review mode keep their existing password
// + already-accepted terms, so those checks are skipped when
// `isReviewMode` is true.

export const validateStep1 = ({
  firstName,
  lastName,
  gender,
  dob,
  nationality,
  country,
  email,
  whatsapp,
  hearSource,
  referrerId,
  password,
  confirm,
  agree,
  isReviewMode,
}) => {
  if (!firstName?.trim() || !lastName?.trim()) return false;
  if (!gender) return false;
  if (!dob) return false;
  if (!nationality?.trim()) return false;
  if (!NATIONALITIES.includes(nationality)) return false;
  if (!country?.trim()) return false;
  if (!COUNTRY_NAMES.has(country)) return false;
  if (!isValidEmail(email)) return false;
  if (!isValidWhatsappE164(whatsapp)) return false;
  if (!hearSource) return false;
  // Referral picks require a referrer id so the awards ledger has
  // something to attribute the join reward to.
  if (hearSource === "referral" && !referrerId) return false;
  if (!isReviewMode) {
    if ((password || "").length < 6 || password !== confirm) return false;
    if (!agree) return false;
  }
  return true;
};

// Massage Preference Card gate — used when the guest has strictly ONE
// Massage-category appointment today and the wizard swaps the medical
// step out for the massage card. All 11 health toggles MUST have an
// answer; any "yes" MUST have a non-empty note so the therapist has
// actionable detail.

export const validateStep2Massage = ({ useMassageCard, massage }) => {
  if (!useMassageCard) return true;
  if (!massage?.pressure) return false;
  if (!massage?.agreed_disclaimer) return false;
  const hc = massage?.health_conditions || {};
  for (const k of MASSAGE_HEALTH_REQUIRED) {
    const row = hc[k] || {};
    if (row.answer !== "yes" && row.answer !== "no") return false;
    if (row.answer === "yes" && !(row.note || "").trim()) return false;
  }
  return true;
};

// The wizard step count depends on which mode the guest lands in.
// Review mode has TWO steps (Profile + Medical Review); other
// skip-medical paths (massage-only day, no bookings) collapse to
// ONE step; classic new-guest still has TWO (Profile + Medical);
// Massage Preference Card path also has TWO (Profile + Massage).
export const computeLastStep = ({ useMassageCard, isReviewMode, skipMedical }) => {
  if (useMassageCard) return 2;
  if (isReviewMode) return 2;
  if (skipMedical) return 1;
  return 2;
};

// ── QR check-in token ───────────────────────────────────────────────
// The staff-facing "Check-In Guest" button mints a single-use QR token
// and renders it centered on the dialog. These two helpers isolate
// the API surface so any future kiosk / receipt-printer wire-in reuses
// the exact same call pattern.
//
// `mintCheckinQrToken` returns `{ link, expires_at, ... }` (the server
// response body), or throws so the caller can render the error.
//
// `fetchCustomerCheckinState` returns the latest customer document so
// the dialog can poll `checked_in_at` and auto-close the moment the
// guest finishes on their phone. Handles both response shapes
// (`{ customer: {...} }` and a bare doc) so callers don't have to.

export const mintCheckinQrToken = async (customerId) => {
  const r = await staffApi.post(`/customers/${customerId}/checkin/link`, { channel: "qr" });
  const link = (r.data && r.data.link) ? String(r.data.link) : "";
  if (!link) throw new Error("Server did not return a check-in link.");
  return r.data;
};

export const fetchCustomerCheckinState = async (customerId) => {
  const r = await staffApi.get(`/customers/${customerId}`);
  return r.data?.customer || r.data || {};
};
